<script setup>
import SettingsItem from "@/components/NewsPage/NewsPageAside/SettingsItem.vue";
import SettingsItemRegions from "@/components/NewsPage/NewsPageAside/SettingsItemRegions.vue";
import SettingsItemImportance from "@/components/NewsPage/NewsPageAside/SettingsItemImportance.vue";
import SettingsItemSentiment from "@/components/NewsPage/NewsPageAside/SettingsItemSentiment.vue";
import SettingsItemCategories from "@/components/NewsPage/NewsPageAside/SettingsItemCategories.vue";
import SettingsItemTags from "@/components/NewsPage/NewsPageAside/SettingsItemTags.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import SettingsItemDate from "./NewsPageAside/SettingsItemDate.vue";
import { ref, provide, defineProps, defineEmits } from "vue";
import { t } from "@/controllerLK/AvalLocalization";
import SettingsItemObject from "@/components/NewsPage/NewsPageAside/SettingsItemObject.vue";
import CrossIconBold from "@/components/Icons/CrossIconBold.vue";
import SettingsItemPoliticalAffiliation from "@/components/NewsPage/NewsPageAside/SettingsItemPoliticalAffiliation.vue";
import ChevronDown from "@/components/Icons/ChevronDown.vue";
import SidebarToggleIcon from "@/components/Icons/SidebarToggleIcon.vue";

defineProps({
  collapsed: { type: Boolean, default: false }
});
defineEmits(["toggle"]);

const containerAside = ref(null);
provide("containerRef", containerAside);
const store = useNewsFeedStore();

const newsFeedStore = useNewsFeedStore();

const resetFilters = async () => {
  newsFeedStore.clearNewsFeed();
  newsFeedStore.clearFacet();
  await newsFeedStore.getNews();
};
</script>
<template>
  <aside
    id="containerAside"
    ref="containerAside"
    class="container-aside scroll"
    :class="{ 'container-aside--collapsed': collapsed }"
  >
    <!-- Свёрнутое состояние: узкая полоса, вся кликабельна.
         Наверху — chevron (повёрнут вправо, «раскрыть»),
         посередине — вертикальная надпись «Фильтры». -->
    <button
      v-if="collapsed"
      class="container-aside__expand"
      :title="t('settings_reset.title')"
      @click="$emit('toggle')"
    >
      <ChevronDown class="container-aside__expand-chevron" />
      <span class="container-aside__expand-label">Фильтры</span>
    </button>
    <div
      v-else
      class="container-aside__wrapper"
    >
      <!-- Кнопка сворачивания — прижата к правому верхнему углу панели.
           Иконка «панель со шторкой» — визуальная метафора сворачивания сайдбара. -->
      <button
        class="container-aside__collapse"
        @click="$emit('toggle')"
      >
        <SidebarToggleIcon class="container-aside__collapse-icon" />
      </button>
      <!--   Календарь   -->
      <SettingsItemDate />
      <!--   Города   -->
      <SettingsItem
        class="container-aside__item"
        :item="store.filterRegion"
        :component="SettingsItemRegions"
        :settings="true"
      />
      <!--   Значимость   -->
      <SettingsItem
        class="container-aside__item"
        :item="store.filterImportance"
        :component="SettingsItemImportance"
      />
      <!--   Тональность   -->
      <SettingsItem
        class="container-aside__item"
        :item="store.filterSentiment"
        :component="SettingsItemSentiment"
      />
      <!--   Категории   -->
      <SettingsItem
        v-if="0"
        class="container-aside__item"
        :item="store.filterCategories"
        :component="SettingsItemCategories"
      />
      <!--   Теги   -->
      <SettingsItem
        v-if="store.facetParams?.tags?.length"
        class="container-aside__item"
        :item="store.filterTags"
        :component="SettingsItemTags"
        :settings="true"
      />
      <!--   Политическая принадлежность. Пока не поддержана ms.cards — показываем выключенной. -->
      <SettingsItem
        class="container-aside__item"
        :item="store.filterPoliticalAffiliation"
        :component="SettingsItemPoliticalAffiliation"
        :disabled="true"
      />
      <!--   Объекты (persons/locations/organisations). Пока не поддержаны ms.cards — выключено. -->
      <SettingsItem
        id="settings-item-object"
        :item="store.filterObject"
        class="container-aside__item"
        :component="SettingsItemObject"
        :settings="true"
        :disabled="true"
      />
    </div>
    <!--  Сброс    -->
    <div
      v-if="!collapsed"
      class="container-aside__reset"
    >
      <AvalButtonRoundedPrimary
        class="container-aside__reset-button"
        size="small"
        @click="resetFilters"
      >
        {{ t('settings_reset.title') }}
        <CrossIconBold class="container-aside__reset-icon" />
      </AvalButtonRoundedPrimary>
    </div>
  </aside>
</template>
<style lang="scss" scoped>
.container-aside {
  padding: 0 5px 0 0;
  scrollbar-gutter: stable;
  position: relative;

  // Свёрнутое состояние: aside превращается в узкую полоску с одной кнопкой.
  // Ширина колонки задаётся в grid родителя (NewsPage.vue).
  &--collapsed {
    padding: 0;
    overflow: hidden;
  }

  // Кнопка «свернуть» в шапке развёрнутой панели — плавает поверх содержимого,
  // не влияя на разметку фильтров.
  &__collapse {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 3;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--icon-icon-primary);
    border-radius: 6px;
    cursor: pointer;

    &:hover {
      background: var(--background-bg-hover);
    }
  }

  // Кнопка «развернуть» в свёрнутом состоянии — занимает всю высоту полосы.
  // Chevron сверху, вертикальная надпись — по центру.
  &__expand {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    padding: 12px 0 12px 0;
    background: var(--background-bg-white);
    color: var(--icon-icon-primary);
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: var(--background-bg-hover);
    }

    // ChevronDown повёрнут на -90° → указывает вправо («раскрыть»).
    &-chevron {
      transform: rotate(-90deg);
      flex-shrink: 0;
    }

    // Вертикальная надпись «Фильтры» — располагается в центре полосы.
    // writing-mode + rotate дают читаемый снизу-вверх текст.
    &-label {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
      font-size: 13px;
      font-weight: 500;
      color: var(--text-text-main);
      letter-spacing: 0.5px;
      -webkit-user-select: none;
      -moz-user-select: none;
      user-select: none;
      margin: auto 0;
    }
  }

  &__reset-button {
    width: 100%;
  }

  &__reset {
    display: flex;
    justify-content: center;
    align-items: center;
    position: sticky;
    bottom: 0;
    width: calc(100%);
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    background: var(--background-bg-white);
    height: 60px; // 30px (height) + 20px (padding) + 10px (gap)
    padding: 0 20px;
    z-index: 2;

    &-icon {
      width: 14px;
      height: 14px;
    }
  }

  &__wrapper {
    background: var(--background-bg-white);
    // padding-top увеличен, чтобы контент (календарь) не заезжал под кнопку сворачивания в правом верхнем углу.
    padding: 37px 20px 0 20px;
    display: flex;
    gap: 20px;
    flex-direction: column;
    min-height: 100%;
    height: max-content;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
}
</style>
